
import React, { useState, useRef, useEffect} from "react";
import {
    FolderKanbanIcon,
    MoreHorizontal,
    Users,
    Database,
    ChevronDown,
    Trash2Icon,
    UserRoundSearchIcon,
    LucideSpeech,
    LucideCircleCheck,
    Ban

} from "lucide-react";

import {toast} from "react-toastify";
import {useDispatch, useSelector} from "react-redux";
import {NavLink, useNavigate} from "react-router-dom";
import {AnimatePresence, motion} from "framer-motion";
import CustomLoaderSmall from "../../../src/components/Layout/CustomLoaderSmall";
import {assets} from "../../../src/assets/assets";
import {
    useFetchAssignedProjectByUserQuery,
    useDeleteProjectMutation,
    useCreateProjectMutation, useUpdateProjectMutation
} from "../../../src/features/projectApiSlice";
import {logout} from "../../../src/features/authSlice";
import {useLogoutMutation} from "../../../src/features/userApiSlice";
import NavigationSideBar from "../../../src/components/Layout/Graph & Tables/NavigationSideBar";
import Helmet from "react-helmet";

export default function ProjectSelectionScreen() {
    const [openProject, setOpenProject] = useState(false);
    const [projectName, setProjectName] = useState<string>("");
    const [description, setDescription] = useState<string>("");
    const [projectNumber, setProjectNumber] = useState<string>("");
    const [location, setLocation] = useState<string>("");
    const [contractor, setContractor] = useState<string>("");

    const [editingProjectId, setEditingProjectId] = useState<string | null>(null);
    const {userInfo} = useSelector((state: any) => state.auth);
    const [activeMenu, setActiveMenu] = useState<string | null>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const [searchTerms, setSearchTerms] = useState("");

    const SELECTED_PROJECT_KEY = "ksolution_selected_project";

    const [selectedProject, setSelectedProject] = useState<string | null>(() => {
        return localStorage.getItem(SELECTED_PROJECT_KEY);
    });

    const userId = userInfo?.id;
    const {data: projects, isLoading: isProjectLoading, isError: isProjectError, refetch} = useFetchAssignedProjectByUserQuery(userId);
    const navigate = useNavigate();
    const [deleteProject] = useDeleteProjectMutation();
    const [createProject] = useCreateProjectMutation();

    const [updateProject, {isLoading: isUpdatingProject}] = useUpdateProjectMutation();

    const handleSelectedProject = (projectId: string) => {
        setSelectedProject(projectId);
        localStorage.setItem(SELECTED_PROJECT_KEY, projectId);
    }

    const dispatch = useDispatch();
    const [logoutApiCall] = useLogoutMutation();

    const handleNavigationToProject = (projectId: string) => {
        if(!selectedProject) {
            toast.warning("Please select a project before continuing.");
            return;
        }

        if (selectedProject !== projectId) {
            toast.warning("Please select a project before continuing.");
            return;
        }

        navigate(`/management/employeeMain/${projectId}`)
    }

    // Confirm if the user exists
    const requireAuth = (callback: () => void) => {
        if (!userInfo) {
            toast.error("You must be logged in to create a project");
            return;
        }
        callback();
    }

    const filterByAssignedProject = projects?.filter((item: any) => item.projectName.toLowerCase().includes(searchTerms.toLowerCase()));

    // Edit Project
    const onSelectProjectEdit = (project: any) => {

        setEditingProjectId(project?.projectId);

        setProjectName(project.projectName);
        setProjectNumber(project.projectNumber);
        setLocation(project.location);
        setDescription(project.description);
        setContractor(project.contractor);

        setOpenProject(true);
        setActiveMenu(null);

        refetch();
    };

    const handleProjectUserSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        const terms = e.target.value.toLowerCase();
        setSearchTerms(terms);
    }

    // Delete Project
    const onSelectProjectDelete = async (id: string) => {
        if (window.confirm("This will permanently delete the project")) {
            try {
                await deleteProject(id).unwrap();
                refetch();

                toast.success("Project deleted successfully.");

            } catch (error) {
                toast.error("Problem with deleting this project now!");
            }
        }
        setActiveMenu(null);
    }

    const logoutHandler = async () => {
        try {
            //@ts-ignore
            await logoutApiCall().unwrap();
            // @ts-ignore
            dispatch(logout())

            navigate("/");

        } catch (error) {
            console.log(error);
        }
    }

    const signOutHandler = async () => {

        try {
            // wait for 4 seconds before executing code
            await new Promise(resolve => setTimeout(resolve, 2000));

            // Enable User Logout
            logoutHandler();

        } catch (error:any) {
            console.error(error);
            toast.error("An error occurred.");
        }
    }

    const EllipsisEdit = [
        {name: "Edit", icon: ChevronDown, color: "#6366f1", action: onSelectProjectEdit},
        {name: "Delete", icon: Trash2Icon, color: "#6366f1", action: onSelectProjectDelete},
    ]

    // when user click outside the ellipsis, the window closes
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target as Node)
            ) {
                setActiveMenu(null);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };

        refetch()
    }, []);

    const onCreateProjectHandler = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (!userInfo) {
            toast.error("You must be logged in to create a project");
            return;
        }

        try {
            if (editingProjectId) {
                await updateProject({
                    id: editingProjectId,
                    projectName: projectName,
                    description: description,
                    projectNumber: projectNumber,
                    location: location,
                    contractor: contractor,
                }).unwrap();

                setOpenProject(false);
                setActiveMenu(null);

                setProjectName("");
                setLocation("");
                setDescription("");
                setContractor("");
                setProjectNumber("");


                await refetch();

                toast.success("Project updated successfully.");

            } else {
                const newProject = {
                    projectName: projectName,
                    projectNumber: projectNumber,
                    location: location,
                    description: description,
                    contractor: contractor,
                    userId: userInfo?.id,
                }
                await createProject(newProject).unwrap();

                toast.success("Project created!");

                await refetch();

                setOpenProject(false);
                setEditingProjectId(null);

                setProjectName("");
                setLocation("");
                setDescription("");
                setContractor("");
                setProjectNumber("");
            }

        } catch (error: any) {
            toast.error("Problem with creating a project");
        }
    }

    return (
        <main>
            <Helmet>
                <title>
                    Employee Project Dashboard
                </title>

                <meta
                    name="dashboard"
                    content="Employee Project Dashboard"
                />

            </Helmet>

        <div className={"bg-gray-50 text-gray-900 min-h-dvh flex"}>

            {selectedProject && (
                <div className={"hidden lg:block"}>
                    <NavigationSideBar />
                </div>
            )}

            <div className={"flex-1 flex min-w-0 flex-col"}>

                <NavLink to={"/"} className={" py-4 px-2 flex items-center hover:text-gray-700"}>
                    <img
                        src={assets.k_Solution_logo}
                        alt="K Solution"
                        className="w-[100px] sm:w-[100px] rounded-md shadow-2xl"
                    />
                </NavLink>

                {/* Header */}
                <section className="
                            w-full
                            max-w-[1600px]
                            mx-auto
                            px-4
                            sm:px-6
                            md:px-8
                            lg:px-10
                            py-5
                            sm:py-6
                            md:py-8

                            pb-[env(safe-area-inset-bottom)]
                            ">

                    {/* Action - Create Project */}
                    <div className="grid grid-cols-2 gap-4 mb-6">

                        <button className="bg-white  p-4 rounded-2xl flex items-center gap-4 shadow-sm cursor-pointer hover:bg-gray-400 transition-colors ease-in-out mb-2"
                                onClick={() => requireAuth(() => setOpenProject(true))}>

                            <div className="p-3 bg-gray-100 rounded-xl">
                                <Database size={20} />
                            </div>
                            <div>
                                <div className="font-medium text-lg items-start">Add Project</div>
                                <div className="text-sm text-gray-500">
                                    Create Project To Get Started
                                </div>
                            </div>
                        </button>

                        <button className="bg-white p-4 rounded-2xl flex items-center gap-4 shadow-sm cursor-pointer hover:bg-gray-400 transition-colors ease-in-out mb-2">
                            <NavLink to={`assign/${selectedProject}`}>
                                <div>
                                    <div className="font-medium text-lg items-start">Employee Assignment</div>
                                    <div className="text-sm text-gray-500">
                                        Assign Employee To A Project
                                    </div>
                                </div>
                            </NavLink>
                            <div className="p-3 bg-gray-100 rounded-xl">
                                <LucideSpeech size={20} />
                            </div>

                        </button>
                    </div>

                    {/* Tab Item */}
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex gap-4 text-sm">
                            <button className="font-medium">All projects</button>
                            <button className="text-gray-500">Your Projects</button>
                            <button className="text-gray-500">Shared with you</button>
                        </div>


                        <section className={"hidden md:block"}>
                            <div className={"flex flex-row items-center gap-2"}>
                                <UserRoundSearchIcon className={"my-2 relative left-10"} color={"gray"} size={24}/>
                                <input
                                    type='text'
                                    onChange={handleProjectUserSearch}
                                    placeholder='Search...'
                                    className='bg-gray-200 text-gray-700 text-sm placeholder-gray-400 rounded-lg pl-16 pr-6 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500'
                                />
                            </div>
                        </section>


                    </div>

                    {/* Project Card Lists */}
                    <div className="grid
                                    grid-cols-1
                                    sm:grid-cols-2
                                    md:grid-cols-3
                                    lg:grid-cols-4
                                    gap-4
                                    mb-6
                                    ">
                        {filterByAssignedProject?.map((pro, i) => (
                            <div className="bg-white p-6 rounded-2xl hover:bg-gray-200 transition-colors mb-2 cursor-pointer"
                                 key={`${pro}-${i}`}>

                                <div key={pro.id}
                                     className={`cursor-pointer rounded-xl border p-4 transition-colors ${
                                         selectedProject === pro.projectId ? "border-green-500 bg-green-50" : "border-gray-300 bg-white hover: bg-gray-200"
                                     }`}>
                                    <button type={"button"}
                                            className={"block w-full text-left"}
                                            onClick={() => handleNavigationToProject(pro.projectId)}>

                                        <div className="flex justify-center mb-6">
                                            <div className="p-12 bg-gray-100 rounded-xl">
                                                {pro.projectName ? <FolderKanbanIcon size={28} color={"gray"}/> : <Users size={20} />}
                                            </div>
                                        </div>

                                        <div className="font-medium text-md">{pro.projectName}</div>

                                        <div className="text-sm text-gray-800 font-medium mt-1">
                                            # {pro.projectNumber}
                                        </div>

                                        <div className="text-sm text-gray-800 font-medium mt-1">
                                            {pro.contractor}
                                        </div>

                                        <div className="text-sm text-gray-800 font-medium mt-1">
                                            {pro.location}
                                        </div>

                                    </button>
                                </div>


                                <div className={"flex flex-row justify-items-end"}>
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setActiveMenu(prev => prev === pro.id ? null : pro.id);
                                        }}>
                                        <MoreHorizontal size={20} className="relative right-2 text-gray-800 hover:bg-gray-400 rounded-md mx-2 transition-colors mb-2"/>
                                    </button>

                                 <div className={"mt-4"}>
                                     <button
                                         type={"button"}
                                         onClick={() => handleSelectedProject(pro.projectId)}
                                         className={"flex w-full items-center mx-3 justify-center gap-2 rounded-lg" +
                                             `px-5 py-2 font-medium transition-colors ${selectedProject === pro.projectId ?
                                         "bg-green-400 text-green-700 ring-2 shadow rounded-lg ring-green-700" :
                                         "bg-gray-100 text-gray-900 ring-2 shadow rounded-lg hover:bg-blue-200"}`}>
                                         {selectedProject === pro.projectId ? (
                                             <>
                                                 <LucideCircleCheck size={24} />
                                                 Selected Project
                                             </>

                                         ) : <>
                                             <Ban size={24} />
                                             Select Project
                                         </>}
                                     </button>
                                 </div>

                                </div>

                                {/* Edit Project Window */}
                                {activeMenu === pro.id && (
                                    <div className={"fixed z-60"}>
                                        <div
                                            ref={dropdownRef}
                                            className={"bg-gray-600 w-96 rounded-2xl shadow-xl p-1 cursor-pointer hover:bg-gray-300 transition-colors max-w-fit"}>

                                            {EllipsisEdit.map((item, index) => (
                                                <motion.div
                                                    key={index}
                                                    onClick={() => item.action(pro)}
                                                    className="flex items-center p-4 text-sm font-medium rounded-lg hover:bg-gray-200 transition-colors mb-2"
                                                >
                                                    <item.icon size={20} style={{ color: item.color, minWidth: "20px" }} />
                                                    <AnimatePresence>
                                                        {activeMenu === pro.id && (
                                                            <motion.span
                                                                key={index}
                                                                className="ml-4 whitespace-nowrap"
                                                                initial={{ opacity: 0, width: 0 }}
                                                                animate={{ opacity: 1, width: "auto" }}
                                                                exit={{ opacity: 0, width: 0 }}
                                                                transition={{ duration: 0.5, delay: 0.3 }}
                                                            >
                                                                {item.name}
                                                            </motion.span>
                                                        )}
                                                    </AnimatePresence>
                                                </motion.div>
                                            ))}
                                        </div>

                                    </div>)}
                            </div>


                        ))}
                    </div>

                    {isProjectError && (
                        <button className={"flex bg-red-300 p-2 rounded-full min-h-2.5 cursor-pointer"}>
                            <span onClick={() => signOutHandler()}
                                  className="px-2 text-red-600 text-lg">Error Loading Project Data. Click to Return</span>
                        </button>
                    )}

                    {isUpdatingProject && (
                        <>
                            <CustomLoaderSmall />
                        </>
                    )}

                    {isProjectLoading && (
                        <>
                            <CustomLoaderSmall />
                        </>
                    )}


                    {/* Opening create project. Add requireAuth and wrap around setOpenEdit if there is no user logged in */}
                    {openProject && (
                        <div className="fixed top-10 left-60 z-40">
                            <div className={"bg-white rounded-2xl shadow-xl p-4 w-[350px]"}>
                                <form className={"space-y-4"}
                                      onSubmit={onCreateProjectHandler}>

                                    <div>
                                        <label className={"block text-md font-medium text-gray-800"}>
                                            Project Name:
                                        </label>
                                        <input
                                            type="text"
                                            required={true}
                                            value={projectName}
                                            placeholder={""}
                                            className={"mt-1 block w-full border border-gray-500 text-gray-900 rounded-lg shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500"}
                                            onChange={(e) => setProjectName(e.target.value)}
                                        />
                                    </div>

                                    <div>
                                        <label className={"block text-md font-medium text-gray-800"}>
                                            Project Number:
                                        </label>
                                        <input
                                            type="text"
                                            required={true}
                                            value={projectNumber}
                                            placeholder={""}
                                            className={"mt-1 block w-full border border-gray-500 text-gray-900 rounded-lg shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500"}
                                            onChange={(e) => setProjectNumber(e.target.value)}
                                        />
                                    </div>

                                    <div>
                                        <label className={"block text-md font-medium text-gray-800"}>
                                            Location:
                                        </label>
                                        <input
                                            type="text"
                                            required={true}
                                            value={location}
                                            placeholder={""}
                                            className={"mt-1 block w-full border border-gray-500 text-gray-900 rounded-lg shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500"}
                                            onChange={(e) => setLocation(e.target.value)}
                                        />
                                    </div>

                                    <div>
                                        <label className={"block text-md font-medium text-gray-800"}>
                                            Client:
                                        </label>
                                        <input
                                            type="text"
                                            required={true}
                                            value={contractor}
                                            placeholder={""}
                                            className={"mt-1 block w-full border border-gray-500 text-gray-900 rounded-lg shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500"}
                                            onChange={(e) => setContractor(e.target.value)}
                                        />
                                    </div>

                                    <div>
                                        <label className={"block text-md font-medium text-gray-800"}>
                                            Description:
                                        </label>
                                        <input
                                            type="text"
                                            required={false}
                                            value={description}
                                            placeholder={""}
                                            className={"mt-1 block w-full border border-gray-500 text-gray-900 rounded-lg shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500"}
                                            onChange={(e) => setDescription(e.target.value)}
                                        />
                                    </div>

                                    <div className="flex justify-end space-x-2">
                                        <button
                                            type="button"
                                            onClick={() => setOpenProject(false)}
                                            className="px-4 py-2 rounded-lg bg-gray-300 hover:bg-gray-400"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            className="px-4 py-2 rounded-lg bg-purple-400 text-white hover:bg-purple-800"
                                        >
                                            Submit
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    )}
                </section>
            </div>

        </div>
        </main>
    )
}